/**
 * Locale-aware currency and number formatters for company detail components.
 * Maps i18n language codes to Intl locale codes.
 *
 * Includes custom compact formatting to fix German locale not abbreviating
 * thousands (CLDR only starts at millions with "Mio.").
 */

const LOCALE_MAP: Record<string, string> = {
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
  ar: 'ar-SA',
  ch: 'zh-CN',
};

const COMPACT_SUFFIXES: Record<string, { k: string; m: string; b: string }> = {
  de: { k: ' Tsd.', m: ' Mio.', b: ' Mrd.' },
  en: { k: 'K', m: 'M', b: 'B' },
  es: { k: ' mil', m: ' M', b: ' mil M' },
  ar: { k: ' ألف', m: ' مليون', b: ' مليار' },
  ch: { k: '千', m: '百万', b: '十亿' },
};

function getLocale(lang: string): string {
  return LOCALE_MAP[lang] || 'en-US';
}

function getSuffixes(lang: string) {
  return COMPACT_SUFFIXES[lang] || COMPACT_SUFFIXES.en;
}

/**
 * Formats a number with consistent compact abbreviations across all locales.
 * Uses locale-appropriate decimal separators but custom suffixes.
 */
function formatCompact(
  value: number,
  lang: string,
  style: 'currency' | 'number',
): string {
  const locale = getLocale(lang);
  const suffixes = getSuffixes(lang);
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  let scaled: number;
  let suffix: string;

  if (abs >= 1_000_000_000) {
    scaled = abs / 1_000_000_000;
    suffix = suffixes.b;
  } else if (abs >= 1_000_000) {
    scaled = abs / 1_000_000;
    suffix = suffixes.m;
  } else if (abs >= 1_000) {
    scaled = abs / 1_000;
    suffix = suffixes.k;
  } else {
    // Below 1000 — no abbreviation needed
    const fmt = new Intl.NumberFormat(locale, {
      ...(style === 'currency' ? { style: 'currency', currency: 'EUR' } : {}),
      maximumFractionDigits: 0,
    });
    return fmt.format(value);
  }

  // Format the scaled number with locale-appropriate decimals
  const numFmt = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  });
  const formattedNum = numFmt.format(scaled);

  if (style === 'currency') {
    // Place € according to locale convention
    if (lang === 'en') {
      return `${sign}€${formattedNum}${suffix}`;
    }
    return `${sign}${formattedNum}${suffix} €`;
  }

  return `${sign}${formattedNum}${suffix}`;
}

export function createFormatters(lang: string) {
  const locale = getLocale(lang);

  const fullCurrency = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });

  const fullNumber = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  });

  return {
    compactCurrency: {
      format: (value: number) => formatCompact(value, lang, 'currency'),
    },
    compactNumber: {
      format: (value: number) => formatCompact(value, lang, 'number'),
    },
    fullCurrency,
    fullNumber,
  };
}
